import { Component, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { RouteParameters, RouteReservedPrefix } from "src/app/constants/route-reserved-prefix";
import { HttpService } from "src/app/services/http.service";
import { CandidateMeetingModel, ConfirmSelfSelectMeetingModel, DeclineInterviewRequest, GetAllMeetingsForCandidateResponse, InterviewerModel } from "../../interviews.models";
import { InterviewStatus, BookingType } from "../../interviews.enums";
import { ErrorHandlerService } from "src/app/services/error-handler.service";
import { ToastEvents } from "talos-core";
import { InterviewsService } from "../../services/interviews.service";
import { BrandingService } from "src/app/services/branding.service";
import { SiteConfig } from "src/app/models/site.config.models";
import { ConfigService } from "src/app/services/config.service";
import { DOCUMENT } from "@angular/common";
import { catchError, concatMap, filter, from, map, Observable, of, take, tap } from "rxjs";
import { GroupSlot, GroupSlots } from "./group-slots.interface";

@Component({
    selector: 'interview-details',
    templateUrl: './interview-details.page.html',
    //styleUrls: ['./referee.page.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class InterviewDetailsPage implements OnInit {
  public branding: SiteConfig|null = null;
  public meeting?: CandidateMeetingModel;
  public interviewers: InterviewerModel[] = [];

  public dashboardInfo$ = this.interviewsService.dashboardInfo$;

  public InterviewStatus = InterviewStatus;
  public BookingType = BookingType;
  public groupMeetingSlots?: GroupSlots;

  constructor(
    private activatedRoute: ActivatedRoute,
    private httpService: HttpService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private interviewsService: InterviewsService,
    private brandingService: BrandingService,
    private configService: ConfigService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
   this.reload();
  }

  private reload(): void {
    // get the id param from the route.
    const idParameter = parseInt(this.activatedRoute.snapshot.params[RouteParameters.INTERVIEW_ID]);
    // set branding
    this.setBranding();
    // get meeting detail set the components data state or handle redirects accordingly
    this.getMeetingDetail(idParameter).subscribe(meetingDetail => {
      this.meeting = meetingDetail;
      if(meetingDetail) {
        this.redirectIfCancelledOrDeclined(meetingDetail);
        this.redirectIfMeetingInThePast(meetingDetail);
        if(this.meeting?.status === InterviewStatus.TENTATIVE && meetingDetail.bookingType === BookingType.GROUP) {
          this.getGroupMeetingSlots(meetingDetail.id).subscribe();
        }
        
      } else {
        this.backToDashboard();
      }
    });     
  }

  private setBranding(): void {
    this.brandingService.branding$.pipe(take(1)).subscribe(b => this.branding = b);
  }

  private reloadMeetings(): Observable<GetAllMeetingsForCandidateResponse> {
    return this.httpService.getAllMeetingsForCandidate().pipe(
      tap(meetings => this.interviewsService.setMeetings(meetings.meetings))
    );
  }

  private getMeetingDetail(id: number): Observable<CandidateMeetingModel | undefined> {
    return this.interviewsService.meetings$.pipe(
      filter(meetings => meetings.length > 0),
      map(meetings => meetings.find(i => i.id == id)),
    );
  }

  private redirectIfCancelledOrDeclined(meeting: CandidateMeetingModel): void {
    if (meeting.status == InterviewStatus.DECLINED || meeting.status == InterviewStatus.CANCELLED) {
      this.backToDashboard();
    }
  }
  private redirectIfMeetingInThePast(meeting: CandidateMeetingModel): void {
    const fromButton = !!history.state?.fromButton;
    const isPastMeeting = this.interviewsService.meetingInThePast(meeting);
    if (isPastMeeting && !fromButton) {
      this.backToDashboard();
    }
  }

  private getGroupMeetingSlots(meetingId: number): Observable<GroupSlots> {
    return this.httpService.getAllGroupMeetingsForCandidate(meetingId).pipe(
      tap(meetingSlots => this.groupMeetingSlots = meetingSlots),
      tap(meetingSlots => this.getInterviewersForMeeting(meetingSlots.groupSlots[0].meetingId).subscribe()),
      catchError(error => {
        this.errorHandler.handleError(error);
        return of(error);
      })
    )
  }

  private getInterviewersForMeeting(meetingId: number): Observable<InterviewerModel[]> {
    return this.httpService.getInterviewersForMeeting(meetingId).pipe(
      tap(interviewers => this.interviewers = interviewers),
      catchError(error => {
        this.errorHandler.handleError(error);
        return of(error);
      })
    )    
  }

  public backToDashboard() {
    this.router.navigateByUrl(RouteReservedPrefix.INTERVIEWS);
  }

  public confirmInterview(meeting: CandidateMeetingModel) {
    this.httpService.confirmInterview(meeting.id).subscribe({
      next: result => {
        meeting.status = InterviewStatus.ACCEPTED;
      },
      error: err => {
        this.errorHandler.handleError(err);
      }
    });
  }

  public confirmGroupInterview(meeting: GroupSlot) {
    this.httpService.confirmGroupInterview(meeting.meetingId).pipe(
      concatMap(_ => this.reloadMeetings()),
      concatMap(_ => from(this.router.navigateByUrl(`interviews/details/${meeting.meetingId}`))),
      tap(_ => this.reload()),
    ).subscribe({
      error: err => this.errorHandler.handleError(err), 
    });
  }

  public declineGroupInterview(declineRequest: DeclineInterviewRequest, afterAccepting = false): void {
    this.httpService.declineGroupInterview(declineRequest, afterAccepting).subscribe({
      next: result => {
        declineRequest.meeting.status = InterviewStatus.DECLINED;
        ToastEvents.dispatchSuccess('Interview Declined', 'This interview has been marked as declined.');
        this.router.navigate([RouteReservedPrefix.INTERVIEWS]);
      },
      error: err => {
        this.errorHandler.handleError(err);
      }
    });
  }

  public declineInterview(declineRequest: DeclineInterviewRequest) {
    this.httpService.declineInterview(declineRequest).subscribe({
      next: result => {
        declineRequest.meeting.status = InterviewStatus.DECLINED;
        ToastEvents.dispatchSuccess('Interview Declined', 'This interview has been marked as declined.');
        this.router.navigate([RouteReservedPrefix.INTERVIEWS]);
      },
      error: err => {
        this.errorHandler.handleError(err);
      }
    });
  }

  public selfSelectSlot(model: ConfirmSelfSelectMeetingModel) {
    this.httpService.confirmSelfSelectedInterview(model.meeting.id, model.slot).subscribe({
      next: result => {
        ToastEvents.dispatchSuccess('Interview Confirmed', 'This interview has been marked as confirmed.');

        this.meeting!.status = result.status;
        this.meeting!.startDate = result.startDate;
        this.meeting!.endDate = result.endDate;
      },
      error: err => {
        this.errorHandler.handleError(err);
      }
    });
  }

  public downloadFile(meetingId: number): void {
    const link = this.document.createElement('a');
    link.href =  `${this.configService.interviewSchedulerApiUrl}/api/portal/addtocalendar/download?meetingId=${meetingId}`;
    link.target = '_blank';
    this.document.body.appendChild(link);
    link.click();
    this.document.body.removeChild(link);
  }
}
